import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ExtractionMonitor.css';

const ExtractionMonitor = () => {
    const [documents, setDocuments] = useState([]);
    const [filteredDocuments, setFilteredDocuments] = useState([]);
    const [selectedMagazine, setSelectedMagazine] = useState('all');
    const [magazines, setMagazines] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStats, setProcessingStats] = useState({
        total: 0,
        processed: 0,
        errors: 0,
        current: null
    });
    const [logs, setLogs] = useState([]);
    const [selectedDocuments, setSelectedDocuments] = useState(new Set());
    const logsEndRef = useRef(null);

    // Scroll to bottom of logs
    const scrollToBottom = () => {
        logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [logs]);

    const fetchDocuments = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v5/documents?limit=1000`);
            const data = await response.json();
            
            if (data.success) {
                setDocuments(data.documents);
                setFilteredDocuments(data.documents);
                
                // Extract unique magazines
                const uniqueMagazines = [...new Set(data.documents.map(doc => `YPN${doc.issue}`))].sort();
                setMagazines(uniqueMagazines);
                
                addLog(`📚 Loaded ${data.documents.length} documents from ${uniqueMagazines.length} magazines`);
            }
        } catch (error) {
            addLog(`❌ Error fetching documents: ${error.message}`, 'error');
        }
    }, []);

    // Fetch documents and magazines on component mount
    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // Filter documents by magazine
    useEffect(() => {
        if (selectedMagazine === 'all') {
            setFilteredDocuments(documents);
        } else {
            const filtered = documents.filter(doc => `YPN${doc.issue}` === selectedMagazine);
            setFilteredDocuments(filtered);
        }
        setSelectedDocuments(new Set()); // Clear selection when filter changes
    }, [selectedMagazine, documents]);

    const addLog = (message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { 
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Unique ID
            timestamp, 
            message, 
            type 
        }]);
    };

    const toggleDocumentSelection = (docId) => {
        setSelectedDocuments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(docId)) {
                newSet.delete(docId);
            } else {
                newSet.add(docId);
            }
            return newSet;
        });
    };

    const selectAllVisible = () => {
        const unextractedDocs = filteredDocuments.filter(doc => !isDocumentExtracted(doc));
        setSelectedDocuments(new Set(unextractedDocs.map(doc => doc.id)));
    };

    const clearSelection = () => {
        setSelectedDocuments(new Set());
    };

    const startExtraction = async () => {
        if (selectedDocuments.size === 0) {
            addLog('⚠️ No documents selected for extraction', 'warning');
            return;
        }

        setIsProcessing(true);
        setProcessingStats({
            total: selectedDocuments.size,
            processed: 0,
            errors: 0,
            current: null
        });

        addLog(`🚀 Starting extraction for ${selectedDocuments.size} documents...`);

        for (const docId of selectedDocuments) {
            const doc = documents.find(d => d.id === docId);
            if (!doc) continue;

            setProcessingStats(prev => ({ ...prev, current: doc }));
            
            // Enhanced logging with document details
            addLog(`📄 Processing Document:`, 'info');
            addLog(`   • ID: ${docId}`, 'info');
            addLog(`   • Title: ${doc.title}`, 'info');
            addLog(`   • Storage Path: ${doc.storage_path || 'N/A'}`, 'info');
            addLog(`   • Magazine: YPN${doc.issue} (${doc.year || 'Unknown Year'})`, 'info');

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/upload/extract/${docId}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                const result = await response.json();

                if (response.ok && result.success) {
                    addLog(`✅ SUCCESS: Extraction completed`, 'success');
                    addLog(`   • Characters: ${result.char_count}`, 'success');
                    addLog(`   • Method: ${result.method}`, 'success');
                    addLog(`   • Quality Score: ${result.quality_score || 'N/A'}`, 'success');
                    addLog(`   • Language: ${result.language || 'en'}`, 'success');
                    
                    setProcessingStats(prev => ({ 
                        ...prev, 
                        processed: prev.processed + 1 
                    }));
                    
                    // Update document status in local state
                    setDocuments(prev => prev.map(d => 
                        d.id === docId ? { ...d, extracted: true, status: 'extracted' } : d
                    ));
                } else {
                    throw new Error(result.error || 'Extraction failed');
                }
            } catch (error) {
                addLog(`❌ FAILED: Extraction error`, 'error');
                addLog(`   • Document: ${doc.title}`, 'error');
                addLog(`   • Error: ${error.message}`, 'error');
                setProcessingStats(prev => ({ 
                    ...prev, 
                    errors: prev.errors + 1 
                }));
            }

            // Add separator between documents
            addLog(`${'─'.repeat(50)}`, 'info');
            
            // Small delay to prevent overwhelming the server
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        setProcessingStats(prev => ({ ...prev, current: null }));
        setIsProcessing(false);
        addLog(`🎉 Extraction complete! Processed: ${processingStats.processed}, Errors: ${processingStats.errors}`);
        
        // Refresh documents to get updated status
        await fetchDocuments();
    };

    const stopExtraction = () => {
        setIsProcessing(false);
        addLog('⏹️ Extraction stopped by user', 'warning');
    };

    const clearLogs = () => {
        setLogs([]);
    };

    // Helper function to check if document is extracted (consistent logic)
    const isDocumentExtracted = (doc) => {
        return doc.extracted || doc.status === 'extracted';
    };

    const getDocumentStatusIcon = (doc) => {
        if (isDocumentExtracted(doc)) return '✅';
        if (selectedDocuments.has(doc.id)) return '🔄';
        return '⏳';
    };

    const getDocumentStatusText = (doc) => {
        if (isDocumentExtracted(doc)) return 'Extracted';
        if (selectedDocuments.has(doc.id)) return 'Selected';
        return 'Pending';
    };

    return (
        <div className="extraction-monitor">
            <div className="monitor-header">
                <h1>📊 Extraction Monitor</h1>
                <p>Monitor and control batch PDF text extraction with detailed logging</p>
            </div>

            <div className="monitor-controls">
                <div className="filter-section">
                    <label htmlFor="magazine-filter">Magazine Filter:</label>
                    <select 
                        id="magazine-filter"
                        value={selectedMagazine} 
                        onChange={(e) => setSelectedMagazine(e.target.value)}
                        disabled={isProcessing}
                    >
                        <option value="all">All Magazines ({documents.length} docs)</option>
                        {magazines.map(mag => {
                            const count = documents.filter(doc => `YPN${doc.issue}` === mag).length;
                            return (
                                <option key={mag} value={mag}>
                                    {mag} ({count} docs)
                                </option>
                            );
                        })}
                    </select>
                </div>

                <div className="selection-controls">
                    <button 
                        onClick={selectAllVisible}
                        disabled={isProcessing}
                        className="btn-secondary"
                    >
                        Select All Unextracted ({filteredDocuments.filter(doc => !isDocumentExtracted(doc)).length})
                    </button>
                    <button 
                        onClick={clearSelection}
                        disabled={isProcessing}
                        className="btn-secondary"
                    >
                        Clear Selection
                    </button>
                </div>

                <div className="extraction-controls">
                    {!isProcessing ? (
                        <button 
                            onClick={startExtraction}
                            disabled={selectedDocuments.size === 0}
                            className="btn-primary"
                        >
                            🚀 Start Extraction ({selectedDocuments.size} selected)
                        </button>
                    ) : (
                        <button 
                            onClick={stopExtraction}
                            className="btn-danger"
                        >
                            ⏹️ Stop Extraction
                        </button>
                    )}
                </div>
            </div>

            {isProcessing && (
                <div className="processing-status">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ 
                                width: `${(processingStats.processed / processingStats.total) * 100}%` 
                            }}
                        ></div>
                    </div>
                    <div className="progress-text">
                        {processingStats.processed} / {processingStats.total} 
                        {processingStats.errors > 0 && ` (${processingStats.errors} errors)`}
                    </div>
                    {processingStats.current && (
                        <div className="current-processing">
                            Currently processing: <strong>{processingStats.current.title}</strong>
                        </div>
                    )}
                </div>
            )}

            <div className="monitor-content">
                <div className="documents-panel">
                    <div className="panel-header">
                        <h3>📄 Documents ({filteredDocuments.length})</h3>
                        <span className="selection-count">
                            {selectedDocuments.size} selected
                        </span>
                    </div>
                    <div className="documents-list">
                        {filteredDocuments.map(doc => (
                            <div 
                                key={doc.id}
                                className={`document-item ${selectedDocuments.has(doc.id) ? 'selected' : ''} ${isDocumentExtracted(doc) ? 'extracted' : ''}`}
                                onClick={() => !isProcessing && toggleDocumentSelection(doc.id)}
                            >
                                <div className="doc-status">
                                    {getDocumentStatusIcon(doc)}
                                </div>
                                <div className="doc-info">
                                    <div className="doc-title">{doc.title}</div>
                                    <div className="doc-meta">
                                        YPN{doc.issue} • ID: {doc.id} • {getDocumentStatusText(doc)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="logs-panel">
                    <div className="panel-header">
                        <h3>📝 Extraction Logs</h3>
                        <button onClick={clearLogs} className="btn-small">Clear Logs</button>
                    </div>
                    <div className="logs-container">
                        {logs.map(log => (
                            <div key={log.id} className={`log-entry log-${log.type}`}>
                                <span className="log-timestamp">{log.timestamp}</span>
                                <span className="log-message">{log.message}</span>
                            </div>
                        ))}
                        <div ref={logsEndRef} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExtractionMonitor;
